import {
  List,
  Datagrid,
  TextField,
  SimpleList,
  TextInput,
  ReferenceField,
  ReferenceInput,
  SelectInput,
  Title,
  useNotify,
  useDataProvider,
  EditButton,
  ListActions,
  ShowButton,
  useStore,
} from "react-admin";
import { Box, Paper, Typography, useMediaQuery } from "@mui/material";
import { useEffect, useState } from "react";
import CustomFilter from "features/CustomFilter";
import { MOBILE_BREAKPOINT, POSTGRADUATE_COURSES, SpecialistServicePath } from "utils/constants";
import { MobileListRow } from "features/MobileListRow";
import { administrationRequests } from "api/administrationApi";

import SecuredComponent from "server/security/SecuredComponent";
import permissions from "app/permissions";
import { checkPermission } from "server/security/utils";
import { getPermissions } from "auth/security/securityUtils";
import FinalizationYearModal from "./Partials/FinalizationYearModal";
import { specialistRequests } from "api/specialistApi";
import { settings } from "app";

const listFilters = [
  <TextInput
    source="name"
    label="Име"
    alwaysOn
  />,
  <ReferenceInput
    source="typeId"
    reference={specialistRequests.getPostgraduateCourseTypesSelect()}
    alwaysOn
  >
    <SelectInput
      label="Тип"
      optionText={"nameBg"}
    />
  </ReferenceInput>,
];

// Shared with the finalization "all" list (ListPostgraduateCoursesAll), which reads the committed year.
export const POSTGRADUATE_FILTERS_KEY = "postgraduateCourses.filters";

const ListPostgraduateCourses = () => {
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const isSmall = useMediaQuery(MOBILE_BREAKPOINT);

  // filters drives the <List>, the gate and the finalize-button check (persisted via useStore);
  // year is the draft bound to CustomFilter, committed on "Провери".
  const [filters, setFilters] = useStore(POSTGRADUATE_FILTERS_KEY, null);
  const [year, setYear] = useState(filters?.year ?? "");
  const [isVisibleFinalizeButton, setIsVisibleFinalizeButton] = useState(false);

  const handleYearSearch = () => {
    if (year && !isNaN(year) && Number(year) >= 2025) {
      setFilters({ year: Number(year) });
    } else if (year && (isNaN(year) || Number(year) < 2025)) {
      notify("Моля, въведете валидна година (2025 или по-голяма).", { type: "error" });
    } else {
      setFilters(null);
      setIsVisibleFinalizeButton(false);
    }
  };

  // Recompute the finalize-button visibility whenever the committed year changes (also on mount when restored).
  useEffect(() => {
    if (!filters?.year) {
      setIsVisibleFinalizeButton(false);
      return;
    }
    (async () => {
      try {
        await dataProvider.customRequest(specialistRequests.getPostgraduateCoursesYearFinalization(filters.year));
        const listResponse = await dataProvider.getList(`${SpecialistServicePath}${POSTGRADUATE_COURSES}`, {
          filter: { year: filters.year },
          pagination: { page: 1, perPage: 1 },
          sort: { field: "id", order: "ASC" },
          meta: { mixed_provider: true },
        });
        setIsVisibleFinalizeButton(listResponse.data.length > 0 && !listResponse.data.find(item => item.number));
      } catch (error) {
        notify("Грешка при заявката за финализиране на година.", { type: "error" });
      }
    })();
  }, [filters?.year, dataProvider, notify]);

  return (
    <>
      {!filters && <Title title={"Следдипломни курсове"} />}
      <Box sx={{ position: "relative" }}>
        <CustomFilter
          title="Филтрирай курсове"
          filters={[
            {
              type: "number",
              label: "Година",
              value: year,
              setValue: setYear,
            },
          ]}
          buttonLabel="Провери"
          buttonDisabled={!year}
          buttonOnClick={handleYearSearch}
        />
        {isVisibleFinalizeButton ? (
          <SecuredComponent permission={permissions.canManageSpecialistPostgraduateCoursesFinalization}>
            <FinalizationYearModal isSmall={isSmall} />
          </SecuredComponent>
        ) : null}
      </Box>
      {!filters && (
        <Paper>
          <Box mt={3}>
            <Typography
              variant="inherit"
              align="center"
              color={"secondary"}
            >
              Въведи данни за търсене на Следдипломни курсове.
            </Typography>
          </Box>
        </Paper>
      )}
      {filters && (
        <List
          filters={listFilters}
          exporter={false}
          queryOptions={{ meta: { mixed_provider: true } }}
          filter={{ year: filters.year }}
          actions={
            <ListActions
              hasCreate={
                isVisibleFinalizeButton &&
                checkPermission(getPermissions(), permissions.canManageSpecialistPostgraduateCourses)
              }
            />
          }
        >
          {isSmall ? (
            <SimpleList
              linkType={false}
              sx={settings.style.mobileList}
              primaryText={record => <MobileListRow fields={[{ label: "Име", value: record.name }]} />}
              secondaryText={record => (
                <div>
                  <MobileListRow fields={[{ label: "Номер", value: record?.number || "N/A" }]} />
                  <MobileListRow fields={[{ label: "Година", value: record.year }]} />
                  <MobileListRow
                    fields={[
                      {
                        label: "Катедра",
                        value: (
                          <ReferenceField
                            source="departmentId"
                            reference={administrationRequests.getDepartmentsSelect()}
                            label="Катедра"
                          />
                        ),
                      },
                    ]}
                  />
                  <MobileListRow fields={[{ label: "Тип", value: record.typeNameBg }]} />
                </div>
              )}
              tertiaryText={() => (
                <>
                  {isVisibleFinalizeButton && (
                    <SecuredComponent permission={permissions.canManageSpecialistPostgraduateCourses}>
                      <EditButton label="" />
                    </SecuredComponent>
                  )}
                  <ShowButton label="" />
                </>
              )}
            />
          ) : (
            <Datagrid
              bulkActionButtons={false}
              rowClick={false}
            >
              <TextField
                source="number"
                label="Номер"
                sortable={false}
              />
              <TextField
                source="year"
                label="Година"
                sortable={false}
              />
              <TextField
                source="name"
                label="Име"
                sortable={false}
              />
              <ReferenceField
                source="departmentId"
                reference={administrationRequests.getDepartmentsSelect()}
                label="Катедра"
              />

              <TextField
                source="typeNameBg"
                label="Тип"
                sortable={false}
              />
              {isVisibleFinalizeButton && (
                <SecuredComponent permission={permissions.canManageSpecialistPostgraduateCourses}>
                  <EditButton label="Редактирай" />
                </SecuredComponent>
              )}
              <ShowButton />
            </Datagrid>
          )}
        </List>
      )}
    </>
  );
};

export default ListPostgraduateCourses;
